import { useState, useEffect } from 'react';
import { MessageSquare, Star, User, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface AdminReplyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reviewId: string | null;
  productName?: string | null;
}

interface ReviewData {
  id: string;
  rating: number;
  comment: string | null;
  admin_reply: string | null;
  created_at: string;
  product_name: string;
  product_image_url: string | null;
}

export const AdminReplyModal = ({
  open,
  onOpenChange,
  reviewId,
  productName,
}: AdminReplyModalProps) => {
  const [review, setReview] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && reviewId) {
      fetchReviewData();
    }
  }, [open, reviewId]);

  const fetchReviewData = async () => {
    if (!reviewId) return;

    try {
      setLoading(true);

      // Fetch review with product info
      const { data: reviewData, error } = await supabase
        .from('reviews')
        .select(`
          id,
          rating,
          comment,
          admin_reply,
          created_at,
          product_id
        `)
        .eq('id', reviewId)
        .single();

      if (error) throw error;

      // Fetch product info
      let productInfo = { name: productName || 'Товар', image_url: null as string | null };
      
      if (reviewData.product_id) {
        const { data: productData } = await supabase
          .from('products')
          .select('name, image_url')
          .eq('id', reviewData.product_id)
          .single();

        if (productData) {
          productInfo = productData;
        }
      }

      setReview({
        id: reviewData.id,
        rating: reviewData.rating,
        comment: reviewData.comment,
        admin_reply: reviewData.admin_reply,
        created_at: reviewData.created_at,
        product_name: productInfo.name,
        product_image_url: productInfo.image_url,
      });
    } catch (error) {
      console.error('Error fetching review:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Ответ на отзыв
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : review ? (
          <div className="space-y-4">
            {/* Product info */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              {review.product_image_url ? (
                <img
                  src={review.product_image_url}
                  alt={review.product_name}
                  className="w-12 h-12 object-cover rounded-lg"
                />
              ) : (
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div>
                <p className="font-medium text-sm">{review.product_name}</p>
                <div className="flex items-center gap-1 mt-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-3.5 w-3.5 ${
                        i < review.rating
                          ? 'text-yellow-500 fill-yellow-500'
                          : 'text-muted-foreground'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* User's review */}
            {review.comment && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Ваш отзыв
                </p>
                <p className="text-sm text-foreground bg-muted/30 p-3 rounded-lg">
                  {review.comment}
                </p>
              </div>
            )}

            {/* Admin reply */}
            {review.admin_reply && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Ответ продавца
                </p>
                <div className="flex gap-3">
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarFallback className="bg-primary/10">
                      <User className="h-4 w-4 text-primary" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 bg-primary/5 border border-primary/10 p-3 rounded-lg">
                    <p className="text-sm">{review.admin_reply}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Date */}
            <p className="text-xs text-muted-foreground text-center pt-2">
              Отзыв от {formatDate(review.created_at)}
            </p>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Не удалось загрузить отзыв</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
